import { useEffect, useRef, useState } from 'react';
import { Box, Flex, Text } from '@cad-arc';
import { SensorAlertThumbnail } from './sensor-alert-thumbnail';
import { sensorEventsPalette } from './sensor-events-palette';

/**
 * How far outside the scroll viewport a clip starts decoding, so it is already moving by the time
 * it scrolls into view rather than popping in.
 */
const PRELOAD_MARGIN = '200px';

/** Poster bitmap for a paused clip. 16:9 to match the box, and small — see the thumbnail. */
const POSTER_WIDTH = 320;
const POSTER_HEIGHT = 180;

/**
 * Whether an element is within `margin` of being visible. Observed against the viewport, which
 * already accounts for the drawer's own scroll clipping, so no scroll container is threaded in.
 */
function useIsNearViewport(ref: React.RefObject<HTMLElement>, margin: string): boolean {
    const [isNear, setIsNear] = useState(false);

    useEffect(
        function observeVisibility() {
            const element = ref.current;
            if (!element) {
                return undefined;
            }
            const observer = new IntersectionObserver(
                function onIntersection([entry]) {
                    setIsNear(entry.isIntersecting);
                },
                { rootMargin: margin }
            );
            observer.observe(element);
            return function disconnect() {
                observer.disconnect();
            };
        },
        [ref, margin]
    );

    return isNear;
}

function ClipChip({ children }: { children: React.ReactNode }): React.ReactElement {
    return (
        <Box
            position="absolute"
            bottom={2}
            left={2}
            paddingX="6px"
            paddingY="3px"
            borderRadius="3px"
            pointerEvents="none"
            style={{ backgroundColor: sensorEventsPalette.overlayBackground }}
        >
            <Text fontFamily="mono" fontSize="10px" fontWeight="semibold" lineHeight="none">
                {children}
            </Text>
        </Box>
    );
}

/**
 * The Sensor Event Card's clip: the alert's animated gif, which loops while the card is on screen.
 *
 * **Why it only animates near the viewport.** A decoded alert gif is ~10.5 MB resident, and the
 * drawer can list the whole capped log. So a clip that is scrolled away renders nothing at all,
 * and memory is bounded by what fits on screen — two or three cards — rather than by the log. The
 * design asks for exactly this ("pause it when it is offscreen"), and here it is also what makes
 * a two-hundred-row list safe.
 *
 * **Why there is no scrubber or `00:03 / 00:08` readout.** The design draws one, but it describes
 * a video. A gif exposes no playback position, so a progress bar here could only be invented. The
 * chip says what is actually knowable: whether it is looping or paused. Pausing shows the first
 * frame, which is the one frame a canvas can draw from an animated image.
 */
export function SensorEventClip({
    imageUrl,
    alt,
}: {
    imageUrl: string | undefined;
    alt: string;
}): React.ReactElement {
    const containerRef = useRef<HTMLButtonElement>(null);
    const isNearViewport = useIsNearViewport(containerRef, PRELOAD_MARGIN);
    const [isPaused, setIsPaused] = useState(false);

    if (!imageUrl) {
        return (
            <Flex
                width="100%"
                alignItems="center"
                justifyContent="center"
                borderRadius="8px"
                style={{
                    aspectRatio: '16 / 9',
                    backgroundColor: sensorEventsPalette.clipBackground,
                    border: `1px solid ${sensorEventsPalette.noClipBorder}`,
                }}
                data-testid="SENSOR_EVENT_CARD_NO_CLIP"
            >
                <Text
                    fontFamily="mono"
                    fontSize="11px"
                    fontWeight="semibold"
                    style={{ color: sensorEventsPalette.noClipText, letterSpacing: '0.12em' }}
                >
                    NO CLIP
                </Text>
            </Flex>
        );
    }

    return (
        <Box
            as="button"
            type="button"
            ref={containerRef}
            position="relative"
            display="block"
            width="100%"
            padding={0}
            border="none"
            borderRadius="8px"
            overflow="hidden"
            cursor="pointer"
            aria-label={isPaused ? 'Play clip' : 'Pause clip'}
            aria-pressed={isPaused}
            onClick={() => setIsPaused((paused) => !paused)}
            style={{ aspectRatio: '16 / 9', backgroundColor: sensorEventsPalette.clipBackground }}
            data-testid="SENSOR_EVENT_CARD_CLIP"
        >
            {!isNearViewport ? undefined : isPaused ? (
                <SensorAlertThumbnail
                    imageUrl={imageUrl}
                    width={POSTER_WIDTH}
                    height={POSTER_HEIGHT}
                    fill
                    alt={alt}
                />
            ) : (
                <img
                    src={imageUrl}
                    alt={alt}
                    decoding="async"
                    draggable={false}
                    style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        // Cover, centred: the design asks never to letterbox a portrait frame.
                        objectFit: 'cover',
                        objectPosition: 'center',
                    }}
                />
            )}
            <ClipChip>{isPaused ? 'PAUSED' : 'LOOP'}</ClipChip>
        </Box>
    );
}
